import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Zap, Users, UserPlus, Trophy, Clock, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PadelPage = () => {
  const [lightboxImage, setLightboxImage] = useState(null);
  const [rulesModalOpen, setRulesModalOpen] = useState(false);

  const openLightbox = (src, alt) => {
    setLightboxImage({ src, alt });
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  const openRulesModal = () => {
    setRulesModalOpen(true);
  };

  const closeRulesModal = () => {
    setRulesModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header scrollY={100} />
      
      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
          >
            <X className="w-10 h-10" />
          </button>
          <img
            src={lightboxImage.src}
            alt={lightboxImage.alt}
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Rules Modal */}
      {rulesModalOpen && (
        <div 
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={closeRulesModal}
        >
          <div 
            className="bg-white rounded-2xl max-w-4xl max-h-[90vh] overflow-y-auto p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeRulesModal}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            
            <div className="pr-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
                <svg className="w-8 h-8 mr-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Règles du Padel
              </h2>

              <div className="space-y-8">
                {/* Position des joueurs */}
                <div className="bg-green-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">1</span>
                    Position des joueurs
                  </h3>
                  <div className="text-gray-700 leading-relaxed space-y-3">
                    <p><strong>Le padel se joue en double.</strong></p>
                    <p>Le joueur qui reçoit peut se placer n'importe où dans son camp, au même titre que son partenaire qui ne reçoit pas le service.</p>
                    <p>Le partenaire du serveur peut également se placer n'importe où dans son propre camp.</p>
                    <p><strong>Seul le serveur doit se trouver derrière la ligne de fond</strong>, à gauche ou à droite de la ligne centrale. Le receveur se place où il veut sur le terrain, dans la diagonale du serveur.</p>
                  </div>
                </div>

                {/* Les points */}
                <div className="bg-blue-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">2</span>
                    Les points
                  </h3>
                  <div className="text-gray-700 leading-relaxed space-y-3">
                    <p>Le Padel emprunte son système de points au tennis. <strong>Il faut 6 jeux pour gagner un set</strong> et l'équipe qui gagne 2 sets remporte la partie.</p>
                    <p>Un jeu se décompose exactement comme au tennis : <strong>15/0, 30/0, 40/0</strong>, avec l'application du point décisif en cas d'égalité à 40A.</p>
                    <p>En cas d'égalité à 6 jeux partout, on procède à un <strong>jeu décisif</strong>, comme au tennis.</p>
                    <p>En cas d'égalité à une manche partout, un <strong>super-jeu décisif à 10 points</strong> sera joué en guise de 3ème manche.</p>
                  </div>
                </div>

                {/* Le service */}
                <div className="bg-orange-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">3</span>
                    Le service
                  </h3>
                  <div className="text-gray-700 leading-relaxed space-y-3">
                    <p>L'engagement s'effectue <strong>à la cuillère après rebond</strong> derrière la ligne de service (sans frapper la balle plus haut que la ceinture), doit être croisé et en commençant de droite à gauche.</p>
                    <p><strong>Service faute :</strong> Un service qui touche le grillage après un rebond est "faute".</p>
                    <p><strong>Service valable :</strong> Le service qui touche le vitrage après le rebond est considéré comme "valable" et l'échange peut se poursuivre.</p>
                    <p><strong>"Let" :</strong> La balle qui effleure la bande du filet et retombe dans le carré de service visé, ou qui touche le grillage après deux rebonds au sol, ou qui tape l'angle du mur à la limite du grillage.</p>
                  </div>
                </div>

                {/* Échange et rebond */}
                <div className="bg-purple-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">4</span>
                    Échange et rebond
                  </h3>
                  <div className="text-gray-700 leading-relaxed space-y-3">
                    <p>À l'exception du service et du retour de service, <strong>une balle peut être prise de volée</strong>. La balle n'a droit qu'à <strong>un seul rebond au sol</strong>.</p>
                    <p>Dans l'échange, après un rebond au sol, la balle peut toucher n'importe quelle autre surface, <strong>excepté le sol de son propre camp</strong>. Ainsi, la poignée de la porte, les grillages et les murs font partie intégrante du terrain.</p>
                    <p><strong>Point perdu :</strong> Le second rebond au sol entraîne la perte du point.</p>
                    <p>La balle peut être reprise <strong>en dehors du court</strong> si et seulement si un 2ème rebond au sol n'a pas été effectué.</p>
                    <p><strong>Point gagnant :</strong> Si, après un rebond dans le camp adverse, la balle revient dans son camp sans avoir été touchée par un adversaire.</p>
                    <p>Une fois la balle en jeu, toutes les balles qui passent le filet doivent d'abord <strong>rebondir sur le sol du camp adverse</strong> avant de toucher une paroi.</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 text-center">
                <button
                  onClick={closeRulesModal}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-xl font-semibold transition-all hover:scale-105"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-green-600 hover:text-green-700 mb-8 transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour à l'accueil
          </Link>
          
          <div className="mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Padel
            </h1>
            <p className="text-xl text-gray-600">
              Découvrez le sport qui monte en Suisse !
            </p>
          </div>

          {/* Description du Padel */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-3xl mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Qu'est-ce que le Padel ?</h2>
            <div className="max-w-4xl">
              <p className="text-gray-700 leading-relaxed mb-4 text-lg">
                Le padel est un sport de raquette qui combine des éléments du tennis et du squash. 
                Joué en double sur un court plus petit entouré de parois vitrées, il est accessible 
                à tous et incroyablement amusant !
              </p>
              <p className="text-gray-700 leading-relaxed text-lg mb-6">
                Sport convivial par excellence, le padel se développe rapidement en Suisse. 
                C'est le sport idéal pour passer un bon moment entre amis ou en famille.
              </p>
              <button
                onClick={openRulesModal}
                className="inline-flex items-center bg-white text-green-600 hover:bg-green-50 px-6 py-3 rounded-xl font-semibold transition-all hover:scale-105 shadow-md hover:shadow-lg"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Règles du jeu
              </button>
            </div>
          </div>

          {/* Galerie de photos du terrain de Padel */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Notre terrain de Padel</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Photo principale - plus grande */}
              <div className="md:col-span-2">
                <div 
                  onClick={() => openLightbox('https://customer-assets.emergentagent.com/job_tennis-cheyres/artifacts/rhjz0vnb_padel.jpg', 'Terrain de padel TC Cheyres-Châbles')}
                  className="relative group cursor-pointer rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_tennis-cheyres/artifacts/rhjz0vnb_padel.jpg"
                    alt="Terrain de padel TC Cheyres-Châbles"
                    className="w-full h-80 md:h-96 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="text-white text-center">
                      <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <p className="font-semibold">Cliquer pour agrandir</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Photos secondaires */}
              <div className="space-y-6">
                <div 
                  onClick={() => openLightbox('https://customer-assets.emergentagent.com/job_clubtennis-update/artifacts/k2fjpzb0_padel2.png', 'Terrain de padel vue 2')}
                  className="relative group cursor-pointer rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_clubtennis-update/artifacts/k2fjpzb0_padel2.png"
                    alt="Terrain de padel vue 2"
                    className="w-full h-36 md:h-44 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
                
                <div 
                  onClick={() => openLightbox('https://customer-assets.emergentagent.com/job_clubtennis-update/artifacts/bkhe4zui_padel3.png', 'Terrain de padel vue 3')}
                  className="relative group cursor-pointer rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_clubtennis-update/artifacts/bkhe4zui_padel3.png"
                    alt="Terrain de padel vue 3"
                    className="w-full h-36 md:h-44 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section Location de matériel - Design professionnel avec fond bleu */}
          <div className="bg-blue-50 border-l-4 border-blue-600 rounded-2xl shadow-lg mb-16 overflow-hidden">
            {/* Titre et ligne bleue - full width */}
            <div className="p-8 pb-0">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Location de matériel</h3>
              </div>
              <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full mb-6"></div>
            </div>
            
            {/* Contenu avec image alignée */}
            <div className="md:flex px-8 pb-8">
              {/* Contenu textuel */}
              <div className="md:w-2/3 pr-6">
                <p className="text-gray-700 leading-relaxed text-lg mb-4">
                  Un service de location de matériel moderne est disponible directement sur place pour votre confort.
                </p>
                <div className="bg-blue-50 p-4 rounded-xl mb-6">
                  <h4 className="font-semibold text-gray-900 mb-2">📦 Équipement disponible :</h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• 4 Raquettes de padel</li>
                    <li>• Balles de padel</li>
                    <li>• Service automatisé 24h/24</li>
                  </ul>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                  <span>Service proposé par</span>
                  <a 
                    href="https://equip.sport/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center bg-blue-600 text-white px-2 py-1 rounded-md font-medium hover:bg-blue-700 transition-colors text-xs"
                  >
                    equip.sport
                    <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
                
                {/* Section téléchargement application */}
                <div className="mt-4">
                  <h5 className="font-medium text-gray-700 mb-2 flex items-center gap-2 text-sm">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    Télécharger l'application
                  </h5>
                  <div className="flex flex-wrap gap-1.5">
                    <a
                      href="https://apps.apple.com/us/app/equip-sport/id1606352048"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center bg-black text-white px-2.5 py-1.5 rounded-md text-xs font-medium hover:bg-gray-800 transition-colors"
                    >
                      <svg className="w-3 h-3 mr-1.5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                      </svg>
                      App Store
                    </a>
                    <a
                      href="https://play.google.com/store/apps/details?id=app.equip"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center bg-green-600 text-white px-2.5 py-1.5 rounded-md text-xs font-medium hover:bg-green-700 transition-colors"
                    >
                      <svg className="w-3 h-3 mr-1.5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M22.018 13.298l-3.919 2.218-4.315-4.252 4.315-4.314 3.919 2.202c.777.444.777 1.702 0 2.146zm-5.025 2.851L9.537 23.06c-.44.44-1.159.44-1.598 0l7.054-6.911zm-6.756-6.756L17.993.485c.44-.44 1.159-.44 1.598 0l-7.054 6.908zm-1.275 1.275L1.029 17.973c-.901-.519-.901-1.375 0-1.894l7.933-4.506z"/>
                      </svg>
                      Google Play
                    </a>
                  </div>
                </div>
              </div>
              
              {/* Image */}
              <div className="md:w-1/3 relative">
                <div 
                  onClick={() => openLightbox('https://customer-assets.emergentagent.com/job_tennis-cheyres/artifacts/647nncia_WhatsApp%20Image%202025-10-11%20at%2011.23.27.jpeg', 'Boîtier de location Equip.sport')}
                  className="relative w-full h-80 cursor-pointer group overflow-hidden rounded-xl shadow-lg"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_tennis-cheyres/artifacts/647nncia_WhatsApp%20Image%202025-10-11%20at%2011.23.27.jpeg"
                    alt="Boîtier de location Equip.sport"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent group-hover:from-black/70 transition-all duration-300"></div>
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <p className="font-semibold mb-1">Boîtier de location</p>
                    <p className="text-sm text-white/90 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      Cliquer pour agrandir
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section WhatsApp Group */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-10 rounded-3xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-10">
              {/* Texte */}
              <div className="flex-1">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Rejoignez le groupe WhatsApp du padel à Cheyres
                </h2>
                <p className="text-gray-700 leading-relaxed text-lg mb-4">
                  Envie de venir essayer le padel ou de trouver des partenaires pour jouer régulièrement ?
                </p>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Rejoignez notre groupe WhatsApp du Padel de Cheyres en scannant simplement le QR code !
                </p>
              </div>

              {/* QR Code avec bouton */}
              <div className="flex-shrink-0">
                <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_7d6b48dd-3a56-4bdf-ab4a-3099abb2356a/artifacts/m4g12f5c_Padel%20qr-code.png"
                    alt="QR Code WhatsApp Padel Cheyres"
                    className="w-56 h-56 object-contain mx-auto mb-6"
                  />
                  <a 
                    href="https://chat.whatsapp.com/DITErzFLOot3IJXMxcECWu?mode=wwt"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-xl font-semibold transition-all hover:scale-105 shadow-md"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    Rejoindre le groupe
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PadelPage;